// École Nid Douillet - Login Page
// Authentication page for directors and parents

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@store/authStore';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import { clsx } from 'clsx';

const LoginPage: React.FC = () => {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading, error, clearError } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Get redirect path from location state
  const from = location.state?.from?.pathname || '/dashboard';

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = t('auth:login.errors.required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('auth:login.errors.invalidEmail');
    }

    if (!formData.password) {
      errors.password = t('auth:login.errors.required');
    } else if (formData.password.length < 6) {
      errors.password = t('auth:login.errors.passwordTooShort');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!validateForm()) {
      return;
    }

    const success = await login(formData);
    if (success) {
      navigate(from, { replace: true });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Clear global error
    if (error) {
      clearError();
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-neutral-900">
          {t('auth:login.title')}
        </h2>
        <p className="mt-2 text-sm text-neutral-600">
          {t('auth:login.subtitle')}
        </p>
      </div>

      {/* Login form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Global error message */}
        {error && (
          <div className="rounded-lg bg-red-50 p-4 border border-red-200">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Email field */}
        <div className="form-group">
          <label htmlFor="email" className="form-label">
            {t('auth:login.email')}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={formData.email}
            onChange={handleInputChange}
            className={clsx(
              'input',
              formErrors.email && 'input-error'
            )}
            placeholder="directeur@niddouillet.ma"
          />
          {formErrors.email && (
            <p className="form-error">{formErrors.email}</p>
          )}
        </div>

        {/* Password field */}
        <div className="form-group">
          <label htmlFor="password" className="form-label">
            {t('auth:login.password')}
          </label>
          <div className="relative">
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={formData.password}
              onChange={handleInputChange}
              className={clsx(
                'input pr-10',
                formErrors.password && 'input-error'
              )}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-3"
            >
              {showPassword ? (
                <EyeSlashIcon className="h-5 w-5 text-neutral-400" />
              ) : (
                <EyeIcon className="h-5 w-5 text-neutral-400" />
              )}
            </button>
          </div>
          {formErrors.password && (
            <p className="form-error">{formErrors.password}</p>
          )}
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={isLoading}
          className="btn btn-primary w-full"
        >
          {isLoading ? (
            <div className="flex items-center justify-center space-x-2">
              <LoadingSpinner size="sm" color="white" />
              <span>Connexion...</span>
            </div>
          ) : (
            t('auth:login.submit')
          )}
        </button>
      </form>

      {/* Demo credentials info */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h3 className="text-sm font-medium text-blue-900 mb-2">
          Comptes de démonstration
        </h3>
        <div className="text-xs text-blue-800 space-y-1">
          <p><strong>Directeur:</strong> director@niddouillet.ma / EcoleNidDouillet2024!</p>
          <p><strong>Parent:</strong> parent@example.com / password123</p>
        </div>
      </div>

      {/* Footer links */}
      <div className="text-center">
        <button
          type="button"
          className="text-sm text-primary-600 hover:text-primary-500"
          onClick={() => {
            // TODO: Implement password reset
            alert('Fonctionnalité à venir');
          }}
        >
          {t('auth:login.forgotPassword')}
        </button>
      </div>
    </div>
  );
};

export default LoginPage;
